import { useLocation } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { SETTINGS_STACK, SettingsSectionSkeleton } from '../settings/settings-section'

// The sessions screens' pending UIs, drawn from the markup they stand in for (admin.sessions.tsx and the
// session page): light, so the route modules that are not code-split stay small.

export const SESSIONS_DESCRIPTION = (
  <>
    Every browser tab&rsquo;s journey from the wide events in ClickHouse: who, which pages, what they clicked, what the
    app asked and answered, and what broke, on one time axis.
  </>
)

export function SessionsPending() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSectionSkeleton title="Sessions" description={SESSIONS_DESCRIPTION}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-8 w-32 rounded-lg" />
            <Skeleton className="h-8 w-40 rounded-lg" />
            <Skeleton className="h-9 w-56 rounded-lg" />
          </div>
          <SessionsListSkeleton />
        </div>
      </SettingsSectionSkeleton>
    </div>
  )
}

export function SessionsListSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border">
      <div className="bg-muted/40 hidden h-[33px] border-b lg:block" />
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex w-48 flex-col gap-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="hidden h-3 flex-1 lg:block" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  )
}

/** A session page: its header, the pages trail, the timeline card and the event list beside it. */
export function SessionDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Skeleton className="size-11 shrink-0 rounded-full" />
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex gap-1">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-7 w-32 rounded-md" />
          ))}
        </div>
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="flex flex-col rounded-2xl border">
            <div className="flex min-h-40 flex-col gap-2 p-4">
              <span className="text-muted-foreground text-[11px] tracking-wide uppercase">On page</span>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
            <div className="flex items-center gap-3 border-y px-4 py-2.5">
              <Skeleton className="size-7 rounded-md" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex flex-col gap-2 px-4 py-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-5 rounded-md" />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-2xl border p-3">
            <Skeleton className="h-6 w-full rounded-full" />
            <Skeleton className="h-8 w-full" />
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/** The layout's pending, which knows the page but not the route: one session, or the list. */
export function SessionsSectionPending() {
  const detail = useLocation({ select: (location) => /\/admin\/sessions\/[^/]+/.test(location.pathname) })
  return detail ? <SessionDetailPending /> : <SessionsPending />
}

export function SessionDetailPending() {
  return (
    <div className={SETTINGS_STACK}>
      <span className="text-primary text-sm">← Sessions</span>
      <SessionDetailSkeleton />
    </div>
  )
}
